import { useCallback, useEffect, useState } from 'react'
import { NavLink, useNavigate, useSearchParams } from 'react-router-dom'
import { ArrowRight, CheckCircle2, CreditCard, Landmark, ShieldCheck } from 'lucide-react'
import { useI18n } from '../i18n'
import { useAuth } from '../auth'
import { useStore } from '../data/store'
import { supabase } from '../lib/supabase'
import {
  BillingError, hasUsableMandate, loadBillingState, startMandateSetup, syncMandate,
  type BillingState, type ContractTerm,
} from '../data/stripe'
import { AboKuendigenSheet } from './AboKuendigung'
import { paketWechseln } from '../data/supabaseAdapter'
import { daysUntil } from '../lib/dates'
import { Button, Card, cx } from '../ui/kit'
import { oeffneExtern } from '../lib/extern'
import {
  preisJeMonat, preisJePeriode, usePakete, usePaketStand, type Paket,
} from '../data/pakete'

/**
 * Payment details, the 30-day trial, and what happens on day 31 — stated before the
 * landlord commits to anything, because that is the deal they are actually signing.
 *
 * Nothing is charged here. Stripe Checkout runs in setup mode: it collects a card or
 * a SEPA mandate, shows the mandate wording in the landlord's language, and hands
 * back. The trial then runs its 30 days, after which the AUVISO CRM raises the
 * invoice into sevDesk and its collection cron takes the money against this mandate.
 */

const TERM_NOTE: Record<ContractTerm, (de: boolean) => string> = {
  monthly: (de) => (de ? 'Monatlich kündbar' : 'Cancel any month'),
  yearly: (de) => (de ? '12 Monate Laufzeit, im Voraus' : '12-month term, paid up front'),
}

function daysLeft(iso: string | null): number | null {
  if (!iso) return null
  return Math.max(0, daysUntil(iso))
}

export function useBillingState() {
  const store = useStore()
  const [state, setState] = useState<BillingState | null>(null)
  const [loading, setLoading] = useState(true)
  const [failed, setFailed] = useState(false)
  const tenantId = store.db.tenant.id

  const reload = useCallback(async () => {
    if (!store.live || !supabase || !tenantId) { setState(null); setLoading(false); return }
    setFailed(false)
    try {
      setState(await loadBillingState(supabase, tenantId))
    } catch { setFailed(true); setState(null) }
    setLoading(false)
  }, [store.live, tenantId])

  useEffect(() => { void reload() }, [reload])
  return { state, loading, failed, reload }
}

export default function BillingSetupView() {
  const { lang, formatMoney } = useI18n()
  const { session, signOut, rolle } = useAuth()
  const store = useStore()
  const navigate = useNavigate()
  const [params] = useSearchParams()
  const de = lang === 'de'

  const { state, loading, failed, reload } = useBillingState()
  const [kuendigungOffen, setKuendigungOffen] = useState(false)
  const [term, setTerm] = useState<ContractTerm>('monthly')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [syncing, setSyncing] = useState(false)

  const returned = params.get('session')
  const cancelled = params.get('cancelled') === '1'

  useEffect(() => { if (state?.contract_term) setTerm(state.contract_term) }, [state?.contract_term])

  // Coming back from Checkout: ask Stripe what actually happened.
  useEffect(() => {
    if (!returned) return
    setSyncing(true)
    void (async () => {
      try {
        await syncMandate(supabase, returned, store.db.tenant.id)
        await reload()
        store.reload()
      } catch (e) {
        setError(e instanceof BillingError ? errorText(e.code, de, e.detail) : String(e))
      }
      setSyncing(false)
    })()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [returned])

  const tenant = store.db.tenant
  const addressComplete = Boolean(tenant.street && tenant.city && (tenant.email || session?.user.email))
  const active = state ? hasUsableMandate(state) : false
  const left = daysLeft(state?.trial_ends_at ?? null)

  // Der eigene Betrag kommt aus dem eigenen Paket. Ist keines zu ermitteln —
  // Demo, noch nicht geladen, Tarif auf Anfrage —, steht hier kein Preis.
  const stand = usePaketStand()
  const { pakete } = usePakete()
  /** Welches Paket gerade gewechselt wird — sperrt die Karten während des Laufs. */
  const [wechsel, setWechsel] = useState<string | null>(null)
  /**
   * Was der letzte Wechsel bewirkt hat.
   *
   * Ohne diese Rückmeldung ist ein vorgemerkter Wechsel ununterscheidbar davon,
   * dass gar nichts passiert ist: Der Knopf wird gedrückt, keine Fehlermeldung
   * erscheint, und die Karte zeigt weiter das alte Paket. René am 03.08.2026:
   * „Ich kann auf das nächstgrößere wechseln, aber nicht wieder zurück." Der
   * Rückwechsel hatte stattgefunden — er stand nur nirgends.
   */
  const [wechselHinweis, setWechselHinweis] = useState<string | null>(null)

  /*
   * Das Paket lässt sich vorwärts wählen, nicht nur erstolpern.
   *
   * Renés Einwand am 01.08.2026: „Ich kann keine anderen Pakete auswählen, was
   * Sinn machen würde, wenn ich weiss, wieviele Objekte ich in Summe brauchen
   * werde." Er hat recht — bis dahin ergab sich das Paket allein aus der Zahl
   * der angelegten Einheiten, und wer auf acht hinauswollte, musste sich bei
   * jeder Wohnung einzeln hochstufen lassen.
   *
   * Entschieden wird trotzdem in der Datenbank: `paket_wechseln` prüft
   * Zugehörigkeit, Rolle und ob das Ziel den Bestand deckt. Die Oberfläche
   * blendet ein zu kleines Paket nur aus und sagt warum — der Knopf ist die
   * Erklärung, nicht die Regel.
   */
  const paketWaehlen = async (code: string) => {
    if (!supabase) return
    setWechsel(code); setError(null); setWechselHinweis(null)
    try {
      const erg = await paketWechseln(supabase, store.db.tenant.id, code)
      store.reload()
      const ziel = pakete.find((p: Paket) => p.code === (erg.plan_pending_code ?? erg.plan_code))
      const name = ziel ? (ziel.name[lang] ?? ziel.name.de ?? ziel.code) : code
      if (erg.wirkt === 'zum_periodenende' && erg.plan_pending_at) {
        // Der einzige Fall, der eine Erklärung braucht: Die Karte zeigt weiter
        // das alte Paket, und das ist richtig so — bezahlt ist bis zum
        // Periodenende das alte.
        const ab = new Date(erg.plan_pending_at).toLocaleDateString(lang === 'de' ? 'de-AT' : 'en-GB')
        setWechselHinweis(de
          ? `${name} ist vorgemerkt und gilt ab ${ab}. Bis dahin bleibt Ihr bisheriges Paket bestehen — Sie haben es bezahlt.`
          : `${name} is scheduled and takes effect on ${ab}. Until then your current plan stays — you have paid for it.`)
      } else if (erg.wirkt === 'sofort') {
        setWechselHinweis(de ? `${name} gilt ab sofort.` : `${name} is active now.`)
      }
    } catch (e) {
      setError(String((e as { message?: string })?.message ?? e))
    }
    setWechsel(null)
  }
  const eigenerPreis = stand.paket ? preisJeMonat(stand.paket, term) : null
  const danach = eigenerPreis !== null && stand.paket
    ? (de
      ? ` Danach ${formatMoney(eigenerPreis, stand.paket.currency)} pro Monat zzgl. USt.`
      : ` Then ${formatMoney(eigenerPreis, stand.paket.currency)} per month plus VAT.`)
    : ''

  const gesperrt = rolle !== 'owner' || !state || loading || failed || state.cancel_at_period_end
    || state.pay_channel === 'apple' || state.pay_channel === 'google'
  const begin = async () => {
    if (gesperrt) return
    setBusy(true)
    setError(null)
    try {
      const setup = await startMandateSetup(supabase, { contractTerm: term, tenantId: store.db.tenant.id })
      /*
       * Im Browser wird die Seite verlassen; Stripe bringt den Vermieter über
       * `/billing/return` zurück, und der Effekt oben gleicht ab. In der Hülle
       * kehrt dieser Aufruf zurück, sobald der eingebettete Browser geschlossen
       * wird — dort gibt es kein `/billing/return`, das die Anwendung sieht,
       * deshalb wird hier von Hand abgeglichen.
       *
       * `syncMandate` ohne Sitzungskennung ist zulässig: die Function sucht
       * dann die jüngste Sitzung des Mandanten. Genau der Fall, der hier
       * vorliegt — der Vermieter war weg, wir wissen nicht, was er getan hat.
       */
      await oeffneExtern(setup.url)
      setSyncing(true)
      try {
        await syncMandate(supabase, null, store.db.tenant.id)
        await reload()
        store.reload()
      } catch (e) {
        setError(e instanceof BillingError ? errorText(e.code, de, e.detail) : String(e))
      }
      setSyncing(false)
      setBusy(false)
    } catch (e) {
      setError(e instanceof BillingError ? errorText(e.code, de, e.detail) : String(e))
      setBusy(false)
    }
  }

  if (syncing) {
    return (
      <div className="p-6 text-center text-muted">
        {de ? 'Zahlungsdaten werden geprüft …' : 'Checking your payment details …'}
      </div>
    )
  }

  // The demo has no account behind it, so it cannot have a mandate either. Offering
  // a payment button here would fail on the way to Stripe — say so instead, and put
  // the way out on the same screen.
  if (!store.live) {
    return (
      <div className="max-w-2xl mx-auto p-4 space-y-4">
        <header className="pt-safe">
          <h1 className="text-xl font-semibold">
            {de ? 'Ihr StaySuite-Abo' : 'Your StaySuite subscription'}
          </h1>
          <p className="text-sm text-muted mt-1">
            {de
              ? 'Sie sehen die Demo mit Beispieldaten. Hier gibt es kein Konto und keine Zahlung.'
              : 'You are in the demo with sample data. There is no account and no payment here.'}
          </p>
        </header>

        <PlanCards term={term} setTerm={setTerm} de={de} />

        <Card className="p-4 space-y-3">
          <p className="text-sm">
            {de
              ? 'Für eine echte Testphase legen Sie ein Konto an: 30 Tage kostenlos, Zahlungsdaten hinterlegen Sie beim Einrichten — abgebucht wird erst danach.'
              : 'For a real trial, create an account: 30 days free, payment details are added during set-up and nothing is charged until the trial ends.'}
          </p>
          <Button variant="primary" onClick={signOut}>
            {de ? 'Konto anlegen' : 'Create an account'} <ArrowRight size={16} />
          </Button>
        </Card>
      </div>
    )
  }

  return (
    <div className="max-w-2xl mx-auto p-4 space-y-4">
      <header className="pt-safe">
        <h1 className="text-xl font-semibold">
          {de ? 'Ihr StaySuite-Abo' : 'Your StaySuite subscription'}
        </h1>
        <p className="text-sm text-muted mt-1">
          {active
            ? (de
              ? 'Ihre Zahlungsdaten liegen vor. Während der Testphase wird nichts abgebucht.'
              : 'Your payment details are on file. Nothing is charged during the trial.')
            : (de
              ? '30 Tage kostenlos testen. Zahlungsdaten hinterlegen Sie jetzt — abgebucht wird erst nach der Testphase.'
              : 'Free for 30 days. You add payment details now — nothing is charged until the trial ends.')}
        </p>
      </header>

      {cancelled && !active && (
        <p className="text-sm bg-warn-soft text-warn rounded-xl px-3 py-2">
          {de
            ? 'Abgebrochen — es wurde nichts hinterlegt. Sie können jederzeit neu starten.'
            : 'Cancelled — nothing was saved. You can start again at any time.'}
        </p>
      )}

      {active && (
        <Card className="p-4 flex items-start gap-3">
          <CheckCircle2 className="text-ok shrink-0 mt-0.5" size={20} />
          <div className="text-sm">
            <p className="font-medium">
              {de ? 'Zahlungsmittel hinterlegt' : 'Payment method on file'}
              {state?.payment_method_type === 'sepa_debit'
                ? de ? ' · SEPA-Lastschrift' : ' · SEPA Direct Debit'
                : state?.payment_method_type === 'card'
                  ? de ? ' · Kreditkarte' : ' · Card'
                  : ''}
            </p>
            <p className="text-muted mt-0.5">
              {left !== null && left > 0
                ? (de
                  ? `Testphase läuft noch ${left} Tage.${danach}`
                  : `${left} days of trial left.${danach}`)
                : (de ? 'Testphase beendet.' : 'Trial has ended.')}
            </p>
          </div>
        </Card>
      )}

      {/* The deal, in the order a landlord asks it. */}
      <PlanCards
        term={term} setTerm={setTerm} de={de}
        eigenes={stand.paket?.code ?? null}
        einheiten={stand.einheiten}
        onWaehlen={rolle === 'owner' && state && !state.cancel_at_period_end && !gesperrt ? (c) => void paketWaehlen(c) : undefined}
        termGesperrt={state !== null && state.status !== 'trialing'}
        waehlend={wechsel}
      >
        <ul className="text-sm text-muted space-y-1.5 pt-1">
          <li className="flex gap-2">
            <ShieldCheck size={16} className="shrink-0 mt-0.5" />
            {de
              ? '30 Tage kostenlos. Es wird nichts abgebucht, solange die Testphase läuft.'
              : '30 days free. Nothing is debited while the trial runs.'}
          </li>
          <li className="flex gap-2">
            <CreditCard size={16} className="shrink-0 mt-0.5" />
            {de
              ? 'Kreditkarte oder SEPA-Lastschrift — die Eingabe erfolgt bei Stripe, nicht bei uns.'
              : 'Card or SEPA Direct Debit — entered at Stripe, never here.'}
          </li>
          <li className="flex gap-2">
            <Landmark size={16} className="shrink-0 mt-0.5" />
            {de
              ? 'Nach der Testphase stellt AUVISO die Rechnung und bucht per Mandat ab.'
              : 'After the trial AUVISO issues the invoice and collects by mandate.'}
          </li>
        </ul>
      </PlanCards>

      {!addressComplete && (
        <Card className="p-4 space-y-2">
          <p className="text-sm bg-warn-soft text-warn rounded-xl px-3 py-2">
            {de
              ? 'Für die Rechnung fehlen noch Anschrift und E-Mail-Adresse.'
              : 'The invoice address and email are still missing.'}
          </p>
          <Button variant="secondary" onClick={() => navigate('/settings')}>
            {de ? 'Rechnungsangaben ergänzen' : 'Complete invoice details'} <ArrowRight size={16} />
          </Button>
        </Card>
      )}

      {wechselHinweis && (
        <p className="text-sm bg-success-soft text-success rounded-xl px-3 py-2">{wechselHinweis}</p>
      )}

      {error && (
        <p className="text-sm bg-danger-soft text-danger rounded-xl px-3 py-2">{error}</p>
      )}

      {failed && <p role="alert" className="text-danger text-sm">{de ? 'Der Abostand konnte nicht geladen werden. Bitte erneut laden.' : 'The subscription could not be loaded. Please reload.'}</p>}
      {rolle !== 'owner' && <p className="text-sm text-muted">{de ? 'Nur die Inhaberin oder der Inhaber kann das Abo abschließen.' : 'Only the owner can subscribe.'}</p>}
      {state?.cancel_at_period_end && <p className="text-sm text-warn">{de ? 'Ihr Abo ist zur Kündigung vorgemerkt. Verwalten Sie zuerst die Kündigung.' : 'Your subscription is scheduled to end. Manage the cancellation first.'}</p>}
      {(state?.pay_channel === 'apple' || state?.pay_channel === 'google') && <p className="text-sm text-muted">{de ? 'Ihr Abo wird über den App Store verwaltet.' : 'Your subscription is managed through the app store.'}</p>}
      {stand.paket && eigenerPreis !== null && !active &&
      <Card className="p-4 text-sm space-y-2">
        <p className="font-medium">{stand.paket.name[lang] ?? stand.paket.name.de}</p>
        <p>{de ? 'Nach der Testphase: ' : 'After the trial: '}{formatMoney(preisJePeriode(stand.paket, term).betrag!, stand.paket.currency)} {term === 'yearly' ? (de ? 'pro Jahr' : 'per year') : (de ? 'pro Monat' : 'per month')} {de ? 'zzgl. USt.' : 'plus VAT'}</p>
        <p>{TERM_NOTE[term](de)}. {de ? 'Der Abschluss erfolgt mit der Bestätigung bei Stripe.' : 'The subscription is concluded when you confirm at Stripe.'}</p>
        <NavLink to="/agb" className="underline">{de ? 'Vertragsbedingungen lesen' : 'Read subscription terms'}</NavLink>
      </Card>}
      <Button
        variant="primary"
        className="w-full"
        disabled={busy || !addressComplete || gesperrt || !stand.paket || eigenerPreis === null}
        onClick={begin}
      >
        {busy
          ? (de ? 'Weiterleitung zu Stripe …' : 'Redirecting to Stripe …')
          : active
            ? (de ? 'Zahlungsmittel ersetzen' : 'Replace payment method')
            : (de ? 'Abo abschließen' : 'Subscribe')}
      </Button>

      {rolle === 'owner' && <Button variant="secondary" onClick={() => setKuendigungOffen(true)}>{de ? 'Abo und Kündigung verwalten' : 'Manage subscription and cancellation'}</Button>}
      {kuendigungOffen && <AboKuendigenSheet onClose={() => { setKuendigungOffen(false); void reload() }} />}

      <p className="text-xs text-muted text-center">
        {de
          ? 'Angebot richtet sich ausschließlich an Unternehmer. Alle Preise zzgl. USt.'
          : 'For business customers only. All prices plus VAT.'}
      </p>
    </div>
  )
}

/** „1–3 Einheiten", „ab 16" — die Bandkanten so, wie ein Vermieter sie liest. */
export function bandText(p: Paket, de: boolean): string {
  const einheiten = de ? 'Einheiten' : 'units'
  if (p.max_units === null) return `${de ? 'ab' : 'from'} ${p.min_units} ${einheiten}`
  if (p.max_units === p.min_units) return `${p.min_units} ${de ? 'Einheit' : 'unit'}`
  return `${p.min_units}–${p.max_units} ${einheiten}`
}

/**
 * Vertragsform und Pakete — in der Demo und im echten Konto dieselbe Darstellung.
 *
 * Die Beträge kommen zur Laufzeit aus `plans`; hier steht keiner. Lassen sie
 * sich nicht laden, erscheint kein Preis und kein Ersatzwert, sondern ein Satz,
 * der sagt, dass gerade keiner zu haben ist.
 *
 * Der Jahresbetrag steht bei der Jahreswahl MIT auf der Karte. Ohne ihn liest
 * ein Vermieter den Monatswert und bekommt eine Rechnung über zwölf Monate —
 * beides richtig, und trotzdem eine Überraschung.
 */
function PlanCards({
  term, setTerm, de, eigenes, einheiten, onWaehlen, waehlend, children, termGesperrt = false,
}: {
  term: ContractTerm
  termGesperrt?: boolean
  setTerm: (t: ContractTerm) => void
  de: boolean
  /** Das gebuchte Paket, damit es hervorgehoben werden kann. */
  eigenes?: string | null
  /**
   * Wie viele Einheiten der Betrieb heute hat. Nur zum ERKLÄREN, warum ein
   * kleineres Paket nicht wählbar ist — verhindert wird es ohnehin serverseitig
   * (`paket_wechseln`, Migration 0061). Eine Sperre, die nur im Knopf sitzt,
   * gilt nicht für den nächsten Aufrufer; eine Begründung, die nur im Server
   * sitzt, erreicht niemanden.
   */
  einheiten?: number
  /**
   * Ist gesetzt, sind die Karten wählbar. Ohne sie zeigen sie nur an — so bleibt
   * dieselbe Darstellung für die Demo und für den Anmeldebildschirm, wo es noch
   * keinen Mandanten gibt, dessen Paket man wechseln könnte.
   */
  onWaehlen?: (code: string) => void
  waehlend?: string | null
  children?: React.ReactNode
}) {
  const { formatMoney } = useI18n()
  const { pakete, geladen } = usePakete()

  return (
    <Card className="p-4 space-y-3">
      <h2 className="font-medium">{de ? 'Vertragsform' : 'Contract term'}</h2>
      <div className="grid sm:grid-cols-2 gap-3">
        {(['monthly', 'yearly'] as ContractTerm[]).map((k) => (
          <button
            key={k}
            type="button"
            disabled={termGesperrt}
            onClick={() => setTerm(k)}
            className={cx(
              'text-left rounded-2xl border p-4 transition',
              term === k ? 'border-accent bg-accent-soft' : 'border-border bg-surface-2',
            )}
          >
            <div className="font-medium">
              {k === 'monthly'
                ? (de ? 'Monatsabo' : 'Monthly')
                : (de ? 'Jahresvertrag' : 'Annual')}
            </div>
            <p className="text-xs text-muted mt-1">{TERM_NOTE[k](de)}</p>
          </button>
        ))}
      </div>

      <h2 className="font-medium pt-1">{de ? 'Pakete' : 'Plans'}</h2>
      {geladen && pakete.length === 0 && (
        <p className="text-sm text-muted">
          {de
            ? 'Die Pakete lassen sich gerade nicht laden. Bitte später noch einmal ansehen — ein Preis, der hier stünde, wäre geraten.'
            : 'The plans cannot be loaded right now. Please look again later — a price shown here would be a guess.'}
        </p>
      )}
      <div className="grid sm:grid-cols-2 gap-3">
        {pakete.map((p) => {
          const jeMonat = preisJeMonat(p, term)
          const periode = preisJePeriode(p, term)

          /*
           * Wählbar ist ein Paket, wenn es die heutigen Einheiten deckt.
           *
           * Ein GRÖSSERES darf man jederzeit nehmen — wer weiß, dass er auf acht
           * Einheiten hinauswill, soll Paket 2 gleich buchen können, statt sich
           * bei jeder Wohnung neu hochzustolpern. Ein KLEINERES als der eigene
           * Bestand geht nicht: es hieße, für Einheiten nicht zu zahlen, die
           * angelegt sind — und niemandem wird eine Einheit weggenommen.
           *
           * „Auf Anfrage" ist kein Paket, das man anklickt, sondern ein Gespräch.
           */
          const deckt = einheiten === undefined || p.max_units === null
            || einheiten <= p.max_units
          const waehlbar = Boolean(onWaehlen) && p.price_base_month !== null
            && p.code !== eigenes && deckt
          const laeuft = waehlend === p.code

          return (
            <button
              key={p.code}
              type="button"
              disabled={!waehlbar || Boolean(waehlend)}
              onClick={waehlbar ? () => onWaehlen?.(p.code) : undefined}
              className={cx(
                'text-left rounded-2xl border p-4 transition w-full',
                p.code === eigenes ? 'border-accent bg-accent-soft' : 'border-border bg-surface-2',
                waehlbar && 'hover:border-accent cursor-pointer',
                !waehlbar && onWaehlen && p.code !== eigenes && 'opacity-60',
              )}
            >
              <div className="flex items-baseline gap-2">
                <span className="font-medium">{de ? p.name.de : p.name.en ?? p.name.de}</span>
                {p.code === eigenes && (
                  <span className="text-xs text-accent">{de ? 'Ihr Paket' : 'Your plan'}</span>
                )}
              </div>
              <p className="text-xs text-muted mt-0.5">{bandText(p, de)}</p>
              {jeMonat === null ? (
                <p className="text-sm mt-2">{de ? 'Auf Anfrage' : 'On request'}</p>
              ) : (
                <>
                  <div className="flex items-baseline gap-1 mt-2">
                    <span className="text-2xl font-semibold tabular-nums">
                      {formatMoney(jeMonat, p.currency)}
                    </span>
                    <span className="text-sm text-muted">{de ? '/ Monat' : '/ month'}</span>
                  </div>
                  {term === 'yearly' && periode.betrag !== null && (
                    <p className="text-xs text-muted mt-1">
                      {formatMoney(periode.betrag, p.currency)} {de ? 'pro Jahr' : 'per year'}
                    </p>
                  )}
                  <p className="text-xs text-muted mt-1">
                    {de ? 'zzgl. USt.' : 'plus VAT'}
                  </p>
                </>
              )}

              {/* Warum dieses Paket gerade nicht geht. Ein ausgegrautes Feld
                  ohne Begründung ist die unfreundlichste Art, nein zu sagen. */}
              {onWaehlen && !deckt && p.code !== eigenes && (
                <p className="text-xs text-warn mt-2">
                  {de
                    ? `Zu klein — Sie haben ${einheiten} Einheiten angelegt.`
                    : `Too small — you have ${einheiten} units.`}
                </p>
              )}
              {onWaehlen && waehlbar && (
                <p className="text-xs text-accent mt-2">
                  {laeuft
                    ? (de ? 'Wird gewechselt …' : 'Switching …')
                    : (de ? 'Dieses Paket wählen' : 'Choose this plan')}
                </p>
              )}
            </button>
          )
        })}
      </div>
      {children}
    </Card>
  )
}

/** Named codes read very differently to a landlord than to an operator. */
/**
 * Fehlertext für den Vermieter.
 *
 * `detail` ist das, was die Gegenstelle wirklich gesagt hat — bei Stripe etwa
 * „No such customer" oder „payment_method_types is not supported". Bis zum
 * 31.07.2026 wurde es weggeworfen, und auf dem Bildschirm stand nur
 * „(stripe_error). Bitte erneut versuchen." Das ist die unfreundlichste Art
 * eines Fehlers: er kennt seine Ursache und verschweigt sie.
 *
 * Es steht deshalb bei den Codes, die wir NICHT übersetzen können — dort ist es
 * die einzige Auskunft, die es gibt. Bei den bekannten Codes bleibt der erklärte
 * Satz allein stehen, weil er mehr sagt als jede Meldung von Stripe.
 */
function errorText(code: string, de: boolean, detail?: string | null): string {
  switch (code) {
    case 'subscription_cancellation_pending':
      return de ? 'Bitte zuerst die vorgemerkte Kündigung verwalten.' : 'Please manage the pending cancellation first.'
    case 'subscription_other_channel':
      return de ? 'Dieses Abo wird über den App Store verwaltet.' : 'This subscription is managed through the app store.'
    case 'contract_term_locked':
      return de ? 'Die Laufzeit eines bestehenden Vertrags bleibt beim Zahlungsmittelwechsel bestehen.' : 'Replacing a payment method keeps the existing contract term.'
    case 'checkout_session_stale':
    case 'checkout_plan_changed':
    case 'checkout_confirmation_failed':
      return de ? 'Der Abostand hat sich geändert. Bitte laden Sie die Seite neu und starten Sie den Abschluss erneut.' : 'The subscription has changed. Reload this page and start again.'
    case 'tenant_has_no_billing_email':
      return de ? 'Es fehlt eine E-Mail-Adresse in den Rechnungsangaben.' : 'Your invoice details have no email address.'
    case 'tenant_has_no_billing_name':
      return de ? 'Es fehlt der Firmenwortlaut in den Rechnungsangaben.' : 'Your invoice details have no business name.'
    case 'tenant_has_no_billing_address':
      return de ? 'Es fehlt die Anschrift in den Rechnungsangaben.' : 'Your invoice details have no address.'
    case 'role_may_not_manage_billing':
      return de ? 'Nur die Inhaberin oder der Inhaber kann Zahlungsdaten hinterlegen.' : 'Only the owner can manage payment details.'
    case 'stripe_not_configured':
    case 'server_misconfigured':
      return de
        ? 'Die Zahlungsanbindung ist noch nicht eingerichtet. Bitte AUVISO kontaktieren.'
        : 'Payments are not configured yet. Please contact AUVISO.'
    case 'no_backend':
      return de ? 'In der Demo gibt es keine Zahlung.' : 'The demo has no payment backend.'
    case 'missing_authorization':
    case 'not_authenticated':
      return de
        ? 'Die Anmeldung ist abgelaufen. Bitte melden Sie sich erneut an.'
        : 'Your session has expired. Please sign in again.'
    case 'no_tenant_membership':
    case 'not_a_member_of_tenant':
      return de
        ? 'Dieses Konto gehört zu keinem Betrieb.'
        : 'This account does not belong to a business.'
    default: {
      const grund = (detail ?? '').trim()
      const kopf = de
        ? `Es hat nicht geklappt (${code}).`
        : `That did not work (${code}).`
      const nach = de ? 'Bitte erneut versuchen.' : 'Please try again.'
      return grund ? `${kopf} ${grund} ${nach}` : `${kopf} ${nach}`
    }
  }
}
